import { AlertCircle, CheckCircle, XClose } from "@untitled-ui/icons-react";
import classNames from "classnames";
import { type ReactNode } from "react";

import Button from "../forms/Button";
import Text from "../text/Text";
import FeatureIcon from "./FeatureIcon";

type AlertProps = {
  color?: keyof typeof colors;
  icon?: ReactNode;
  title?: ReactNode;
  children?: ReactNode;
  onClose?: () => void;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "title">;

const colors = {
  primary: "text-primary-700 bg-primary-25 border border-primary-300 shadow-xs",
  warning: "text-warning-700 bg-warning-25 border border-warning-300 shadow-xs",
  success: "text-success-700 bg-success-25 border border-success-300 shadow-xs",
  error: "text-error-700 bg-error-25 border border-error-300 shadow-xs",
  gray: "text-gray-700 bg-gray-25 border border-gray-300 shadow-xs",
  light: "text-gray-700 bg-white border border-gray-200 shadow-lg",
};

const defaultIcons = {
  primary: (
    <FeatureIcon variant="outline" color="primary">
      <CheckCircle className="size-5" />
    </FeatureIcon>
  ),
  warning: (
    <FeatureIcon variant="outline" color="warning">
      <AlertCircle className="size-5" />
    </FeatureIcon>
  ),
  success: (
    <FeatureIcon variant="outline" color="success">
      <CheckCircle className="size-5" />
    </FeatureIcon>
  ),
  error: (
    <FeatureIcon variant="outline" color="error">
      <AlertCircle className="size-5" />
    </FeatureIcon>
  ),
  gray: (
    <FeatureIcon variant="outline" color="gray">
      <CheckCircle className="size-5" />
    </FeatureIcon>
  ),
  light: (
    <FeatureIcon variant="modern" color="gray">
      <CheckCircle className="size-5" />
    </FeatureIcon>
  ),
};

const closeTextColors = {
  primary: "text-primary-500",
  warning: "text-warning-500",
  success: "text-success-500",
  error: "text-error-500",
  gray: "text-gray-500",
  light: "text-gray-500",
};

export default function Alert({
  title,
  children,
  onClose,
  icon,
  color = "primary",
  className,
  ...rest
}: AlertProps) {
  return (
    <div
      className={classNames("relative rounded-xl", colors[color], className)}
      {...rest}
    >
      <div className="flex items-start gap-3 p-4">
        <div className="shrink-0">{icon ? icon : defaultIcons[color]}</div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {title && (
            <Text size="sm" weight="semibold" className="wrap-break-word">
              {title}
            </Text>
          )}
          {children && (
            <div className="text-sm wrap-break-word">{children}</div>
          )}
        </div>
        {onClose && (
          <Button
            size="sm"
            color="link-gray"
            square
            className="-me-1.5 -mt-1.5 shrink-0"
            onClick={() => {
              onClose();
            }}
          >
            <span className="sr-only">Close</span>
            <XClose className={classNames("size-5", closeTextColors[color])} />
          </Button>
        )}
      </div>
    </div>
  );
}
